import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, MapPin, Globe, Phone, Navigation, TrendingUp, TrendingDown, Minus, Star, CheckCircle, Calendar, Loader2 } from 'lucide-react';
import { useBusinessAuth } from '@/context/BusinessAuthContext';

const API_URL = '';

// Metric Card Component
const MetricCard = ({ label, value, trend, icon: Icon }) => {
  const getTrendIcon = () => {
    if (trend > 0) return <TrendingUp className="w-3.5 h-3.5 text-green-500" />;
    if (trend < 0) return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
    return <Minus className="w-3.5 h-3.5 text-slate-400" />;
  };

  const getTrendColor = () => {
    if (trend > 0) return 'text-green-600';
    if (trend < 0) return 'text-red-600';
    return 'text-slate-500';
  };

  return (
    <div className="bg-white rounded-[20px] p-5 shadow-sm shadow-black/5">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-600" />
        </div>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-1 text-xs font-medium ${getTrendColor()}`}>
            {getTrendIcon()}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <p className="text-3xl font-bold text-slate-900 mb-1">{value.toLocaleString()}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
};

// Simple Chart Component
const SimpleChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const maxValue = Math.max(...data.map(d => d.views), 1);
  const chartHeight = 120;

  return (
    <div className="bg-white rounded-[20px] p-5 shadow-sm shadow-black/5">
      <h3 className="text-base font-semibold text-slate-900 mb-4">Views Over Time</h3>
      <p className="text-xs text-slate-400 mb-3">Last 30 days</p>
      
      <div className="relative h-[120px]">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="border-b border-slate-100" />
          ))}
        </div>
        
        {/* Bars */}
        <div className="absolute inset-0 flex items-end justify-between gap-[2px]">
          {data.map((item) => (
            <div
              key={item.date}
              className="flex-1 bg-teal-500 rounded-t-sm transition-all hover:bg-teal-600"
              style={{ 
                height: `${Math.max((item.views / maxValue) * chartHeight, 2)}px`,
                minWidth: '4px'
              }}
              title={`${item.date}: ${item.views} views`}
            />
          ))}
        </div>
      </div>
      
      {/* X-axis labels */}
      <div className="flex justify-between mt-3 text-[10px] text-slate-400">
        <span>{data[0]?.date?.slice(5)}</span>
        <span>{data[Math.floor(data.length / 2)]?.date?.slice(5)}</span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
};

// Listing Health Component
const ListingHealth = ({ data }) => {
  if (!data) return null;

  return (
    <div className="bg-white rounded-[20px] p-5 shadow-sm shadow-black/5">
      <h3 className="text-base font-semibold text-slate-900 mb-4">Listing Health</h3>
      
      <div className="space-y-4">
        {/* Plan */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Plan</span>
          <span className="text-sm font-medium text-slate-900">{data.plan}</span>
        </div>
        
        {/* Status */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Listing Status</span>
          <div className="flex items-center gap-1.5">
            <CheckCircle className={`w-4 h-4 ${data.status === 'Active' ? 'text-green-500' : 'text-slate-400'}`} />
            <span className={`text-sm font-medium ${data.status === 'Active' ? 'text-green-600' : 'text-slate-500'}`}>
              {data.status}
            </span>
          </div>
        </div>
        
        {/* Featured Placement */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Featured Placement</span>
          {data.is_featured ? (
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span className="text-sm font-medium text-amber-600">Active</span>
            </div>
          ) : (
            <span className="text-sm text-slate-400">Not active</span>
          )}
        </div>
        
        {/* Next Billing */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Next Billing</span>
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-600">
              {data.next_billing !== 'N/A' ? 'Monthly' : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PortalAnalyticsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useBusinessAuth();
  
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [demoMode, setDemoMode] = useState(false);

  // Demo data for preview
  const demoAnalytics = {
    metrics: {
      profile_views: { value: 847, trend: 12 },
      map_opens: { value: 234, trend: 8 },
      website_clicks: { value: 156, trend: -3 },
      phone_calls: { value: 89, trend: 15 },
      direction_requests: { value: 67, trend: 5 },
    },
    chart_data: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      views: Math.floor(Math.random() * 40) + 10
    })),
    listing_health: {
      plan: "Core Listing + Featured Boost",
      status: "Active",
      is_featured: true,
      next_billing: "Monthly"
    }
  };

  // Check for demo mode first
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === 'true') {
      setDemoMode(true);
      setAnalytics(demoAnalytics);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Skip auth redirect if in demo mode
    if (demoMode) return;
    
    if (!authLoading && !user) {
      navigate('/partner/login');
    }
  }, [user, authLoading, navigate, demoMode]);

  useEffect(() => {
    if (user && !demoMode) {
      fetchAnalytics();
    }
  }, [user, demoMode]);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/partner/portal/analytics`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  const metrics = analytics?.metrics || {};

  return (
    <div className="min-h-screen bg-[#F2F2F7]" data-testid="portal-analytics-page">
      {/* Header */}
      <header className="bg-white pt-4 pb-5 px-5 rounded-b-[28px] shadow-sm shadow-black/5">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Link 
            to="/portal"
            className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Analytics</h1>
            <p className="text-sm text-slate-500">Last 30 days performance</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="px-5 py-6">
        <div className="max-w-2xl mx-auto space-y-5">
          {/* Top Metrics Grid */}
          <div className="grid grid-cols-2 gap-4">
            <MetricCard 
              label="Profile Views"
              value={metrics.profile_views?.value || 0}
              trend={metrics.profile_views?.trend}
              icon={Eye}
            />
            <MetricCard 
              label="Map Opens"
              value={metrics.map_opens?.value || 0}
              trend={metrics.map_opens?.trend}
              icon={MapPin}
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <MetricCard 
              label="Website Clicks"
              value={metrics.website_clicks?.value || 0}
              icon={Globe}
            />
            <MetricCard 
              label="Phone Calls"
              value={metrics.phone_calls?.value || 0}
              icon={Phone}
            />
            <MetricCard 
              label="Directions"
              value={metrics.direction_requests?.value || 0}
              icon={Navigation}
            />
          </div>

          {/* Views Chart */}
          <SimpleChart data={analytics?.chart_data} />

          {/* Listing Health */}
          <ListingHealth data={analytics?.listing_health} />

          {/* Info Note */}
          <div className="bg-slate-100/50 rounded-[16px] p-4">
            <p className="text-xs text-slate-500 text-center">
              Analytics are updated in real-time as guests interact with your listing.
              Featured businesses receive priority placement in search results.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
